import { ClientResult } from '@vp/client-result';
import { FetchClient, AuthTokenHeader, ILogger } from '@vp/fetch-client';

import { ICombination } from '../../types/interfaces';
import { encodeObject } from '../httpCallUtilities';
import { configuration } from '../serverConfigurationProvider';

const ProductIsConstrainedReason = 'productConfigurationIsConstrained';
const StrategiesWithoutRetirement = { '1': 'ProductPreference', '2': 'LowestPrice' };

export async function completeCombination(
  productKey: string,
  version: number,
  tenant: string,
  locale: string,
  tokenConfig: AuthTokenHeader,
  client: FetchClient,
  logger: ILogger,
  options?: ICombination
): Promise<ICombination | null> {
  const optionsParam = options ? `&${encodeObject(options, 'options')}` : '';
  const url = `${
    configuration.serviceUrls.ranchService
  }api/v1/products/${productKey}/versions/${version}:complete?${encodeObject(
    buildContext(tenant, locale),
    'context'
  )}&requestor=${configuration.requestor}${optionsParam}`;

  try {
    const response = await client.authenticatedGet<RanchResponse>(url, tokenConfig);
    if (response.isFailed) {
      return null;
    }
    const defaultConfiguration = response.result.configurations[0];
    const defaultCombination: ICombination = {};
    defaultConfiguration.options.forEach((option) => {
      defaultCombination[option.name] = option.value;
    });
    return defaultCombination;
  } catch (error) {
    logger.error('Error fetching complete combination:', { error });
  }

  return null;
}

export async function getEnumeration(
  productKey: string,
  version: number,
  tenant: string,
  locale: string,
  tokenConfig: AuthTokenHeader,
  client: FetchClient
): Promise<ClientResult<ICombination[]>> {
  const url = `${
    configuration.serviceUrls.ranchService
  }api/v2/products/${productKey}/versions/${version}:enumerate?requestor=${configuration.requestor}&${encodeObject(
    buildContext(tenant, locale),
    'context'
  )}`;
  const configurations = [];
  let ranchClientResponse = null;
  do {
    const batchUrl: string = `${url}${
      ranchClientResponse?.result?.nextBatchToken
        ? `&nextBatchToken=${ranchClientResponse?.result?.nextBatchToken}`
        : ''
    }`;
    ranchClientResponse = await client.authenticatedGet<RanchEnumerateResponse>(
      batchUrl,
      tokenConfig
    );
    if (ranchClientResponse.isFailed)
      return ClientResult.failed({
        message: ranchClientResponse.errorDetails?.message || 'Ranch request failed',
        response: {
          status: ranchClientResponse.statusCode,
          data: ranchClientResponse.errorDetails,
        },
      });

    if (!ranchClientResponse.result?._embedded?.configurations?.length)
      return ClientResult.failed(ConfigurationsNotFoundError);

    configurations.push(...ranchClientResponse.result._embedded.configurations);
  } while (ranchClientResponse?.result?.nextBatchToken);

  const combinations = configurations.map((combination) =>
    Object.assign({}, ...combination.selectedOptions.map((o) => ({ [o.name]: o.value.value })))
  );

  return ClientResult.successful(combinations);
}

export async function getFulfillmentCombination(
  productKey: string,
  version: number,
  tenant: string,
  locale: string,
  optionSelections: ICombination,
  tokenConfig: AuthTokenHeader,
  client: FetchClient
): Promise<ClientResult<FulfillmentCombination>> {
  const context = buildContext(tenant, locale);

  const url = `${
    configuration.serviceUrls.ranchService
  }api/v1/products/${productKey}/versions/${version}/fulfillment:complete?requestor=${
    configuration.requestor
  }&${encodeObject(context, 'context')}&${encodeObject(optionSelections, 'options')}`;

  let ranchClientResponse = await client.authenticatedGet<RanchFulfillmentResponse>(
    url,
    tokenConfig
  );
  if (allCombinationsConstrained(ranchClientResponse)) {
    ranchClientResponse = await client.authenticatedGet<RanchFulfillmentResponse>(
      `${url}&${encodeObject(StrategiesWithoutRetirement, 'strategies')}`,
      tokenConfig
    );
  }
  if (ranchClientResponse.isFailed)
    return ClientResult.failed({
      message: ranchClientResponse.errorDetails?.message || 'Ranch request failed',
      response: {
        status: ranchClientResponse.statusCode,
        data: ranchClientResponse.errorDetails,
      },
    });
  if (!ranchClientResponse.result?.configurations?.length)
    return ClientResult.failed(ConfigurationsNotFoundError);

  const combinations = toCombination(ranchClientResponse.result.configurations[0]);
  return ClientResult.successful(combinations);
}

function buildContext(tenant: string, locale: string): Context {
  const countryCode = locale.split('-')[1];
  return {
    Merchant: tenant,
    Market: countryCode,
  };
}

export interface RanchResponse {
  configurations: {
    options: {
      name: string;
      value: string;
    }[];
  }[];
}

type Context = {
  Merchant: string;
  Market: string;
};

export interface RanchEnumerateResponse {
  nextBatchToken?: string;
  _embedded?: {
    configurations: EnumerateConfiguration[];
  };
}

interface EnumerateConfiguration {
  selectedOptions: SelectedOption[];
}

interface SelectedOption {
  name: string;
  value: {
    value: string;
  };
}

interface RanchFulfillmentResponse {
  configurations: FulfillmentConfiguration[];
}

interface FulfillmentConfiguration {
  productId: string;
  version: number;
  options: Option[];
}

interface Option {
  name: string;
  value: string;
}

const ConfigurationsNotFoundError = {
  message: 'No configurations found for product',
  response: {
    status: 404,
    data: null,
  },
};

function allCombinationsConstrained(
  ranchClientResponse: ClientResult<RanchFulfillmentResponse>
): boolean {
  const errorDetails = tryParse<{ reason: string }>(ranchClientResponse.errorDetails);
  return (
    ranchClientResponse.statusCode === 400 && errorDetails?.reason === ProductIsConstrainedReason
  );
}

function toCombination(configuration: FulfillmentConfiguration): FulfillmentCombination {
  return {
    productId: configuration.productId,
    version: configuration.version,
    combination: Object.assign({}, ...configuration.options.map((o) => ({ [o.name]: o.value }))),
  };
}

export interface FulfillmentCombination {
  productId: string;
  version: number;
  combination: ICombination;
}

function tryParse<T>(value: unknown): T | null {
  if (typeof value === 'object') {
    return value as T;
  }
  if (typeof value === 'string') {
    try {
      return JSON.parse(value) as T;
    } catch {
      return null;
    }
  }
  return null;
}
